import { create } from 'zustand';

interface FolderItemScrollState {
  newFolderId: string | null;
  requestScrollToNewFolder: (id: string) => void;
  clearNewFolderId: (id: string) => void;
  /** IDs to scroll to and highlight. Scroll hook scrolls to the topmost row. */
  newItemIds: readonly string[];
  /** Full item objects for pinned items (Asset[], Playlist[], etc. — cast at usage site).
   *  Set atomically with newItemIds via requestScrollToNewItems so the display pin and
   *  scroll signal always agree. Persists beyond the scroll animation; cleared only when
   *  queryKey changes (folder nav, sort, search) or on hook unmount. */
  newItemObjects: readonly unknown[];
  /** Sets newItemIds (scroll signal) and newItemObjects.
   *  Pass items (full objects) so displayAssets can inject them before the Apollo cache
   *  is updated. When skipScroll is true, seeds newItemObjects (subscription guard) but
   *  does NOT set newItemIds — caller fires the scroll signal later after a dialog closes. */
  requestScrollToNewItems: (
    ids: string[],
    items?: unknown[],
    opts?: { skipScroll?: boolean },
  ) => void;
  /** Sets newItemIds and clears newItemObjects. Use for the designer path where the asset
   *  was created externally and is not yet in the Apollo cache. Clearing newItemObjects
   *  deactivates the subscription guard so the refetch can bring the asset in naturally. */
  setScrollSignal: (ids: string[]) => void;
  /** Called by the scroll hook once it has animated to the item. */
  clearNewItemIds: (id: string) => void;
  /** Removes specific IDs from newItemObjects without touching the rest.
   *  Use when deleting one item from a batch-pinned set. */
  updateNewItemObjects: (items: unknown[]) => void;
  /** Called when queryKey changes (context reset) or on hook unmount. */
  clearNewItemObjects: () => void;
}

export const useFolderItemScrollStore = create<FolderItemScrollState>((set, get) => ({
  newFolderId: null,
  requestScrollToNewFolder: (id) => set({ newFolderId: id }),
  clearNewFolderId: (id) => set((s) => (s.newFolderId === id ? { newFolderId: null } : s)),
  newItemIds: [],
  newItemObjects: [],
  requestScrollToNewItems: (ids, items, opts) =>
    set((s) => {
      let newObjects: readonly unknown[];
      if (items !== undefined) {
        const newIds = new Set((items as { _id: string }[]).map((o) => o._id));
        newObjects = [
          ...(items as { _id: string }[]),
          ...(s.newItemObjects as { _id: string }[]).filter((o) => !newIds.has(o._id)),
        ];
      } else if (s.newItemObjects.length > 0) {
        // Prepend stubs for any new IDs not already pinned, so they appear at the front.
        // displayAssets resolves stubs via stableMap (Apollo cache) on the next render.
        const existingIds = new Set((s.newItemObjects as { _id: string }[]).map((o) => o._id));
        const freshStubs = ids.filter((id) => !existingIds.has(id)).map((id) => ({ _id: id }));
        newObjects =
          freshStubs.length > 0 ? [...freshStubs, ...s.newItemObjects] : s.newItemObjects;
      } else {
        newObjects = ids.map((id) => ({ _id: id })); // seed stubs so the subscription guard activates
      }
      return { newItemIds: opts?.skipScroll ? [] : ids, newItemObjects: newObjects };
    }),
  setScrollSignal: (ids) => set({ newItemIds: ids, newItemObjects: [] }),
  clearNewItemIds: (id) =>
    set((s) => {
      if (!s.newItemIds.includes(id)) return s;
      return { newItemIds: [] };
    }),
  updateNewItemObjects: (items) => set({ newItemObjects: items }),
  clearNewItemObjects: () => {
    const s = get();
    if (!s.newItemIds.length && !s.newItemObjects.length) return;
    set({ newItemIds: [], newItemObjects: [] });
  },
}));

/** Does this pinned object belong to the entity family the reader expects?
 *
 * `newItemObjects` is ONE global list shared by every feature, and pages routinely mount two
 * different-entity consumers at once: the Playlists page renders the playlist list AND the
 * always-mounted asset picker; the Schedules page renders the schedule sidebar AND the content
 * picker. Uploading in either picker pins an **Asset**, which the playlist/schedule display memos
 * would otherwise cast to their own type and render as a blank "untitled" row (#1976 follow-up).
 *
 * Match on the GraphQL `__typename` PREFIX, not equality: a create mutation returns
 * `PlaylistDetailResponse` / `ScheduleDetailResponse` while the list query returns
 * `Playlist` / `Schedule`, and both shapes reach this store. Apollo stamps `__typename` on every
 * result (nothing in this app sets `addTypename: false`), so an object without one is a
 * hand-built stub — those are already excluded by the callers' key-count check.
 */
export function isPinnedEntity(o: unknown, typenamePrefix: string): boolean {
  const typename = (o as { __typename?: unknown } | null | undefined)?.__typename;
  return typeof typename === 'string' && typename.startsWith(typenamePrefix);
}

/** True for a hand-built `{_id}` stub — no `__typename`, so its entity family is unknown.
 *  Callers that must not drop stubs (their ids do resolve later) pair this with isPinnedEntity. */
export function isPinnedStub(o: unknown): boolean {
  return (o as { __typename?: unknown } | null | undefined)?.__typename === undefined;
}

/** Remove deleted ids from newItemObjects; clear entirely when nothing remains pinned. */
export function purgePinnedAfterDelete(deletedSet: Set<string>): void {
  const store = useFolderItemScrollStore.getState();
  const updated = (store.newItemObjects as { _id: string }[]).filter((o) => !deletedSet.has(o._id));
  if (updated.length === 0) {
    store.clearNewItemObjects();
  } else {
    store.updateNewItemObjects(updated);
  }
}
